import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import Select from 'react-select';
import { AutoSizer, Table, Column } from 'react-virtualized';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
    CartesianGrid, Cell, LabelList
} from 'recharts';
import { formatDate } from '../../../../utils/helpers';

const STATUS_COLOURS = {
    'Confirmed': '#118dff',
    'Allocated': '#FFD700',
    'Notified': '#4CAF50',
    'Not Allocated': '#f44336',
};

const STATUS_ORDER = ['Confirmed', 'Notified', 'Allocated', 'Not Allocated'];

const STATUS_TEXT_COLOURS = {
    'Confirmed': '#fff',
    'Allocated': '#000',
    'Notified': '#fff',
    'Not Allocated': '#fff',
};

const formatTime = (timeVal) => {
    if (!timeVal) return '';
    const str = String(timeVal);
    // Handle "HH:MM:SS" or "HH:MM:SS.nnnnnnn" from C# TimeSpan serialisation
    return str.substring(0, 5);
};


const renderBarLabel = (status, chartDataRef) => (props) => {
    const { x, y, width, height, value, index } = props;
    if (!width || !height || height < 14 || !value || value < 3) return null;

    const dataEntry = chartDataRef[index];
    const count = dataEntry ? (dataEntry[`_${status}_count`] || 0) : 0;
    const pct = Math.round(value);
    const fill = status === 'Allocated' ? '#000' : '#fff';

    if (height < 28) {
        return (
            <text x={x + width / 2} y={y + height / 2} fill={fill}
                textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={600}>
                {count}
            </text>
        );
    }

    return (
        <g>
            <text x={x + width / 2} y={y + height / 2 - 6} fill={fill}
                textAnchor="middle" dominantBaseline="middle" fontSize={11} fontWeight={700}>
                {count}
            </text>
            <text x={x + width / 2} y={y + height / 2 + 7} fill={fill}
                textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={500}>
                {pct}%
            </text>
        </g>
    );
};

const siteSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '4px',
        boxShadow: 'none',
        '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
    }),
    singleValue: (base) => ({ ...base, color: 'white', fontSize: '12px' }),
    placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', fontSize: '12px' }),
    input: (base) => ({ ...base, color: 'white', fontSize: '12px' }),
    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: '#1e163c', border: '1px solid rgba(255,255,255,0.12)' }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        padding: '6px 10px',
        backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.1)' : 'transparent',
        color: 'white',
        '&:active': { backgroundColor: 'rgba(0,93,229,0.2)' }
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
    clearIndicator: (base) => ({ ...base, padding: '4px', color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
};

const FulfillmentSummary = ({ securitySummary, financialWeeks, loading }) => {
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedSite, setSelectedSite] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [searchText, setSearchText] = useState('');
    const [mobileTab, setMobileTab] = useState('chart');
    const [containerWidth, setContainerWidth] = useState(600);
    const chartWrapperRef = useRef(null);
    const isMobileView = isMobile || isTablet;

    useEffect(() => {
        const el = chartWrapperRef.current;
        if (!el || isMobileView) return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [isMobileView]);

    const siteOptions = useMemo(() => {
        if (!securitySummary || securitySummary.length === 0) return [];
        const unique = [...new Set(securitySummary.map(s => s.siteName).filter(Boolean))].sort();
        return unique.map(name => ({ value: name, label: name }));
    }, [securitySummary]);

    const siteFilteredData = useMemo(() => {
        if (!securitySummary || securitySummary.length === 0) return [];
        if (!selectedSite) return securitySummary;
        return securitySummary.filter(shift => shift.siteName === selectedSite.value);
    }, [securitySummary, selectedSite]);

    const chartData = useMemo(() => {
        if (!siteFilteredData || siteFilteredData.length === 0 || !financialWeeks || financialWeeks.length === 0) return [];

        // Pre-compute week boundaries once to avoid repeated Date construction per shift
        const weeks = financialWeeks.map(fw => {
            const start = new Date(fw.weekCommencing);
            const end = new Date(fw.weekEnding);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return { key: fw.weekEnding, start, end, weekCommencing: fw.weekCommencing, weekEnding: fw.weekEnding };
        });

        const periodMap = {};
        weeks.forEach(w => {
            periodMap[w.key] = {
                weekCommencing: w.weekCommencing,
                weekEnding: w.weekEnding,
                label: formatDate(w.weekEnding),
                shifts: [],
            };
        });

        siteFilteredData.forEach(shift => {
            const shiftDate = new Date(shift.shiftDate);
            for (const w of weeks) {
                if (shiftDate >= w.start && shiftDate <= w.end) {
                    periodMap[w.key].shifts.push(shift);
                    break;
                }
            }
        });

        const data = Object.values(periodMap)
            .filter(p => p.shifts.length > 0)
            .sort((a, b) => new Date(a.weekEnding) - new Date(b.weekEnding))
            .map(period => {
                const total = period.shifts.length;
                const counts = {};
                STATUS_ORDER.forEach(s => { counts[s] = 0; });
                period.shifts.forEach(shift => {
                    const status = shift.notificationStatus || 'Not Allocated';
                    if (counts.hasOwnProperty(status)) {
                        counts[status]++;
                    }
                });

                const entry = {
                    periodEnding: period.label,
                    _weekCommencing: period.weekCommencing,
                    _weekEnding: period.weekEnding,
                    _total: total,
                };

                STATUS_ORDER.forEach(status => {
                    const count = counts[status];
                    entry[`_${status}_count`] = count;
                    entry[status] = total > 0 ? (count / total) * 100 : 0;
                });

                return entry;
            });

        return data;
    }, [siteFilteredData, financialWeeks]);

    const filteredChartData = useMemo(() => {
        if (!dateFrom && !dateTo) return chartData;
        if (!chartData.length) return chartData;

        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);

        return chartData.filter(d => {
            const wc = new Date(d._weekCommencing);
            const we = new Date(d._weekEnding);
            wc.setHours(0, 0, 0, 0);
            we.setHours(23, 59, 59, 999);
            // Week overlaps the selected range
            if (from && we < from) return false;
            if (to && wc > to) return false;
            return true;
        });
    }, [chartData, dateFrom, dateTo]);

    const desktopChartWidth = useMemo(() => {
        return Math.max(filteredChartData.length * 70, containerWidth);
    }, [filteredChartData, containerWidth]);

    const tableData = useMemo(() => {
        if (!siteFilteredData || siteFilteredData.length === 0) return [];

        let filtered = siteFilteredData;

        if (selectedFilter) {
            const start = new Date(selectedFilter.weekCommencing);
            const end = new Date(selectedFilter.weekEnding);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);

            filtered = filtered.filter(shift => {
                const shiftDate = new Date(shift.shiftDate);
                const statusMatch = shift.notificationStatus === selectedFilter.status ||
                    (!shift.notificationStatus && selectedFilter.status === 'Not Allocated');
                const dateMatch = shiftDate >= start && shiftDate <= end;
                return statusMatch && dateMatch;
            });
        }

        if (dateFrom || dateTo) {
            const from = dateFrom ? new Date(dateFrom) : null;
            const to = dateTo ? new Date(dateTo) : null;
            if (from) from.setHours(0, 0, 0, 0);
            if (to) to.setHours(23, 59, 59, 999);
            filtered = filtered.filter(shift => {
                const shiftDate = new Date(shift.shiftDate);
                if (from && shiftDate < from) return false;
                if (to && shiftDate > to) return false;
                return true;
            });
        }

        if (searchText.trim()) {
            const term = searchText.trim().toLowerCase();
            filtered = filtered.filter(shift =>
                (shift.staffName && shift.staffName.toLowerCase().includes(term)) ||
                (shift.siteName && shift.siteName.toLowerCase().includes(term)) ||
                (shift.notificationStatus && shift.notificationStatus.toLowerCase().includes(term)) ||
                (shift.shiftID && String(shift.shiftID).includes(term))
            );
        }

        return filtered;
    }, [siteFilteredData, selectedFilter, dateFrom, dateTo, searchText]);

    const totalHrs = useMemo(() => {
        return tableData.reduce((sum, row) => sum + (parseFloat(row.hrs) || 0), 0);
    }, [tableData]);

    const handleBarClick = useCallback((status) => (barData, index) => {
        const clicked = filteredChartData[index];
        if (!clicked) return;

        setSelectedFilter(prev => {
            const isDeselecting = prev && prev.periodEnding === clicked.periodEnding && prev.status === status;
            if (isDeselecting) return null;

            if (isMobileView) setMobileTab('table');

            return {
                periodEnding: clicked.periodEnding,
                status,
                weekCommencing: clicked._weekCommencing,
                weekEnding: clicked._weekEnding,
            };
        });
    }, [filteredChartData, isMobileView]);

    const isBarSelected = (periodEnding, status) => {
        if (!selectedFilter) return false;
        return selectedFilter.periodEnding === periodEnding && selectedFilter.status === status;
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="small-spinner">
                    <span></span><span></span><span></span><span></span>
                </div>
                <p className="mt-3">Loading fulfilment summary...</p>
            </div>
        );
    }

    if (!securitySummary || securitySummary.length === 0) {
        return (
            <div className="text-center p-5" style={{ color: 'white' }}>
                <p>No fulfilment data available.</p>
            </div>
        );
    }

    const renderLegend = () => (
        <div className="fulfilment-summary__legend">
            <span className="fulfilment-summary__legend-title">NotificationStatus</span>
            {STATUS_ORDER.map(status => (
                <div key={status} className="fulfilment-summary__legend-item">
                    <span className="fulfilment-summary__legend-swatch" style={{ background: STATUS_COLOURS[status] }} />
                    {status}
                </div>
            ))}
        </div>
    );

    const mobileChartWidth = isMobileView ? Math.max(filteredChartData.length * 55, 380) : undefined;

    const renderChart = () => (
        <>
            {renderLegend()}
            {isMobileView ? (
                <div className="fulfilment-summary__chart-scroll">
                    <div className="fulfilment-summary__chart-inner" style={{ width: mobileChartWidth }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={filteredChartData}
                                margin={{ top: 10, right: 15, left: 5, bottom: 5 }}
                                barCategoryGap="18%"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                                <XAxis
                                    dataKey="periodEnding"
                                    tick={{ fill: 'white', fontSize: 10 }}
                                    axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                                    tickLine={false}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    dy={5}
                                />
                                <YAxis
                                    tick={{ fill: 'white', fontSize: 10 }}
                                    tickFormatter={(v) => `${v}%`}
                                    domain={[0, 100]}
                                    axisLine={false}
                                    tickLine={false}
                                    width={35}
                                />
                                {STATUS_ORDER.map((status) => (
                                    <Bar
                                        key={status}
                                        dataKey={status}
                                        stackId="a"
                                        fill={STATUS_COLOURS[status]}
                                        name={status}
                                        onClick={handleBarClick(status)}
                                        cursor="pointer"
                                        activeBar={false}
                                    >
                                        <LabelList dataKey={status} content={renderBarLabel(status, filteredChartData)} />
                                        {filteredChartData.map((entry, index) => (
                                            <Cell
                                                key={`${status}-${index}`}
                                                fill={STATUS_COLOURS[status]}
                                                fillOpacity={isBarSelected(entry.periodEnding, status) ? 1 : 0.85}
                                                stroke={isBarSelected(entry.periodEnding, status) ? '#fff' : 'none'}
                                                strokeWidth={isBarSelected(entry.periodEnding, status) ? 2 : 0}
                                            />
                                        ))}
                                    </Bar>
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            ) : (
                <div className="fulfilment-summary__chart-wrapper" ref={chartWrapperRef}>
                    <div className="fulfilment-summary__chart-scroll">
                        <div style={{ width: desktopChartWidth, height: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={filteredChartData}
                                    margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                                    barCategoryGap="12%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                                    <XAxis
                                        dataKey="periodEnding"
                                        tick={{ fill: 'white', fontSize: 10 }}
                                        axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                                        tickLine={false}
                                        angle={-45}
                                        textAnchor="end"
                                        height={90}
                                        dy={10}
                                        label={{ value: 'Period Ending', position: 'insideBottom', offset: 2, fill: 'white', fontSize: 11 }}
                                    />
                                    <YAxis
                                        tick={{ fill: 'white', fontSize: 11 }}
                                        tickFormatter={(v) => `${v}%`}
                                        domain={[0, 100]}
                                        axisLine={false}
                                        tickLine={false}
                                        width={40}
                                    />
                                    {STATUS_ORDER.map((status) => (
                                        <Bar
                                            key={status}
                                            dataKey={status}
                                            stackId="a"
                                            fill={STATUS_COLOURS[status]}
                                            name={status}
                                            onClick={handleBarClick(status)}
                                            cursor="pointer"
                                            activeBar={false}
                                        >
                                            <LabelList dataKey={status} content={renderBarLabel(status, filteredChartData)} />
                                            {filteredChartData.map((entry, index) => (
                                                <Cell
                                                    key={`${status}-${index}`}
                                                    fill={STATUS_COLOURS[status]}
                                                    fillOpacity={isBarSelected(entry.periodEnding, status) ? 1 : 0.85}
                                                    stroke={isBarSelected(entry.periodEnding, status) ? '#fff' : 'none'}
                                                    strokeWidth={isBarSelected(entry.periodEnding, status) ? 2 : 0}
                                                />
                                            ))}
                                        </Bar>
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}
            {isMobileView && (
                <p className="fulfilment-summary__tap-hint">Swipe to see all periods &middot; Tap a bar to filter</p>
            )}
        </>
    );

    const hasActiveFilters = selectedSite || dateFrom || dateTo || searchText.trim() || selectedFilter;

    const handleClearAll = () => {
        setSelectedSite(null);
        setDateFrom('');
        setDateTo('');
        setSearchText('');
        setSelectedFilter(null);
    };

    const renderFilters = () => (
        <div className="fulfilment-summary__site-filter">
            <label className="fulfilment-summary__site-filter-label">Site</label>
            <Select
                value={selectedSite}
                onChange={(val) => { setSelectedSite(val); setSelectedFilter(null); }}
                options={siteOptions}
                placeholder="All Sites"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                styles={siteSelectStyles}
            />
            <label className="fulfilment-summary__site-filter-label" style={{ marginLeft: 8 }}>From</label>
            <input
                type="date"
                className="fulfilment-summary__date-input"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setSelectedFilter(null); }}
            />
            <label className="fulfilment-summary__site-filter-label" style={{ marginLeft: 8 }}>To</label>
            <input
                type="date"
                className="fulfilment-summary__date-input"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setSelectedFilter(null); }}
            />
            <input
                type="text"
                className="fulfilment-summary__search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
            />
            {hasActiveFilters && (
                <button
                    className="fulfilment-summary__clear-btn"
                    onClick={handleClearAll}
                    style={{ marginLeft: 8 }}
                >
                    Clear
                </button>
            )}
        </div>
    );

    const renderTable = () => (
        <>
            {renderFilters()}
            {selectedFilter && (
                <div className="fulfilment-summary__filter-badge">
                    <span>
                        <strong>{selectedFilter.status}</strong> — {selectedFilter.periodEnding}
                    </span>
                    <button
                        className="fulfilment-summary__clear-btn"
                        onClick={() => setSelectedFilter(null)}
                    >
                        Clear
                    </button>
                </div>
            )}
            {isMobileView ? (
                <div className="fulfilment-summary__mobile-cards">
                    {tableData.map((row, i) => (
                        <div key={row.shiftID || i} className="fulfilment-summary__mobile-card">
                            <div className="fulfilment-summary__mobile-card-header">
                                <span className="fulfilment-summary__mobile-card-id">#{row.shiftID}</span>
                                <span
                                    className="fulfilment-summary__status-badge"
                                    style={{
                                        backgroundColor: STATUS_COLOURS[row.notificationStatus] || '#666',
                                        color: STATUS_TEXT_COLOURS[row.notificationStatus] || '#fff',
                                    }}
                                >
                                    {row.notificationStatus}
                                </span>
                            </div>
                            <div className="fulfilment-summary__mobile-card-site">{row.siteName}</div>
                            <div className="fulfilment-summary__mobile-card-details">
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">Staff</span>
                                    <span>{row.staffName}</span>
                                </div>
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">Date</span>
                                    <span>{formatDate(row.shiftDate)}</span>
                                </div>
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">Time</span>
                                    <span>{formatTime(row.shiftStart)} – {formatTime(row.shiftFinish)}</span>
                                </div>
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">Hrs</span>
                                    <span className="fulfilment-summary__mobile-card-hrs">{(parseFloat(row.hrs) || 0).toFixed(2)}</span>
                                </div>
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">SIA Status</span>
                                    <span>{row.lastSIALicenceStatus || ''}</span>
                                </div>
                                <div className="fulfilment-summary__mobile-card-field">
                                    <span className="fulfilment-summary__mobile-card-label">SIA Check</span>
                                    <span>{row.lastSIACheckDate ? formatDate(row.lastSIACheckDate) : ''}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="fulfilment-summary__mobile-total">
                        <span>Total Hours</span>
                        <span>{totalHrs.toFixed(2)}</span>
                    </div>
                </div>
            ) : (
                <div className="fulfilment-summary__virtual-table">
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <Table
                                width={width}
                                height={915}
                                headerHeight={36}
                                rowHeight={32}
                                rowCount={tableData.length}
                                rowGetter={({ index }) => tableData[index]}
                                rowClassName={({ index }) => index < 0 ? 'fulfilment-vt__header' : 'fulfilment-vt__row'}
                                headerClassName="fulfilment-vt__header-cell"
                            >
                                <Column label="ID" dataKey="shiftID" width={60} flexGrow={0} />
                                <Column label="Date" dataKey="shiftDate" width={80} flexGrow={0}
                                    cellRenderer={({ cellData }) => formatDate(cellData)} />
                                <Column label="Site" dataKey="siteName" width={140} flexGrow={1} />
                                <Column label="Staff" dataKey="staffName" width={120} flexGrow={1} />
                                <Column label="Status" dataKey="notificationStatus" width={110} flexGrow={0}
                                    cellRenderer={({ cellData }) => (
                                        <span className="fulfilment-summary__status-badge" style={{
                                            backgroundColor: STATUS_COLOURS[cellData] || '#666',
                                            color: STATUS_TEXT_COLOURS[cellData] || '#fff',
                                        }}>{cellData}</span>
                                    )} />
                                <Column label="Start" dataKey="shiftStart" width={60} flexGrow={0}
                                    cellRenderer={({ cellData }) => formatTime(cellData)} />
                                <Column label="Finish" dataKey="shiftFinish" width={60} flexGrow={0}
                                    cellRenderer={({ cellData }) => formatTime(cellData)} />
                                <Column label="Hrs" dataKey="hrs" width={55} flexGrow={0}
                                    cellRenderer={({ cellData }) => (parseFloat(cellData) || 0).toFixed(2)} />
                                <Column label="Bill Rate" dataKey="billRate" width={70} flexGrow={0}
                                    cellRenderer={({ cellData }) => cellData != null ? `£${(parseFloat(cellData) || 0).toFixed(2)}` : ''} />
                                <Column label="Nett" dataKey="nett" width={70} flexGrow={0}
                                    cellRenderer={({ cellData }) => cellData != null ? `£${(parseFloat(cellData) || 0).toFixed(2)}` : ''} />
                                <Column label="SIA Status" dataKey="lastSIALicenceStatus" width={100} flexGrow={0} />
                                <Column label="SIA Check" dataKey="lastSIACheckDate" width={90} flexGrow={0}
                                    cellRenderer={({ cellData }) => cellData ? formatDate(cellData) : ''} />
                            </Table>
                        )}
                    </AutoSizer>
                    <div className="fulfilment-vt__total-row">
                        <span>Total Hours: {totalHrs.toFixed(2)}</span>
                    </div>
                </div>
            )}
        </>
    );

    if (isMobileView) {
        return (
            <div className="fulfilment-summary fulfilment-summary--mobile">
                <div className="fulfilment-summary__tab-bar">
                    <button
                        className={`fulfilment-summary__tab ${mobileTab === 'chart' ? 'active' : ''}`}
                        onClick={() => setMobileTab('chart')}
                    >
                        Chart
                    </button>
                    <button
                        className={`fulfilment-summary__tab ${mobileTab === 'table' ? 'active' : ''}`}
                        onClick={() => setMobileTab('table')}
                    >
                        Table
                        {selectedFilter && <span className="fulfilment-summary__tab-dot" />}
                    </button>
                </div>
                <div className="fulfilment-summary__mobile-panel">
                    {mobileTab === 'chart' ? renderChart() : renderTable()}
                </div>
            </div>
        );
    }

    return (
        <div className="fulfilment-summary">
            <div className="fulfilment-summary__chart">
                {renderChart()}
            </div>
            <div className="fulfilment-summary__table">
                {renderTable()}
            </div>
        </div>
    );
};

export default FulfillmentSummary;
